import { eurosToCents } from '@/constants'
import { useJobillaOrgId } from '@/hooks'
import { authorisePaymentMethod, toLocalISOString, translatePaymentError } from '@/lib'
import {
	type CompletionStepId,
	completionStepsAtom,
	type CompletionStepStatus,
	initialCompletionSteps,
	logger,
	ReviewCompleteData,
	useCompleteEditor,
} from '@/ui'
import {
	campaignChannel,
	campaignsIndexQueryOptions,
	campaignsShowQueryKey,
	campaignStatusCheck,
	entitlementIndexQueryKey,
	type EntitlementIndexResponse,
	useCampaignStatusReview,
	useCampaignsUpdate,
	useCampaignUpload,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { getRouteApi, useNavigate } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { useSetAtom } from 'jotai'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const companyRoute = getRouteApi('/_auth/company/$companyId')

export function useNewCampaignComplete(campaignId: string) {
	const { setOnReviewComplete, setOnRedirectAfterComplete } = useCompleteEditor()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const jobillaOrgId = useJobillaOrgId()
	const { companyId } = companyRoute.useParams()
	const { t, i18n } = useTranslation()
	const setCompletionSteps = useSetAtom(completionStepsAtom)
	const currencyFormatter = useMemo(
		() => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }),
		[i18n.language],
	)

	const setStep = useCallback(
		(id: CompletionStepId, status: CompletionStepStatus, messages?: string[]) => {
			setCompletionSteps((steps) => steps.map((step) => step.id === id ? { ...step, status, messages } : step))
		},
		[setCompletionSteps],
	)

	const { mutateAsync: bakedInImageUpload } = useCampaignUpload({
		mutationKey: ['upload-campaign-baked-image'],
	})

	const { mutateAsync: campaignMutation } = useCampaignsUpdate({
		mutationKey: ['update-campaign-complete'],
	})

	const { mutateAsync: submitForReviewMutation } = useCampaignStatusReview({
		mutationKey: ['submit-campaign-for-review'],
	})

	useEffect(() => {
		setOnReviewComplete(async (data: ReviewCompleteData) => {
			const hasMetaChannel = data.channels.includes(campaignChannel.META)
			const genericError = t('editor:review.submissionFailedMessage')

			setCompletionSteps(initialCompletionSteps())

			// Step 1 — Save: upload baked images (if any) then persist budget, dates
			// and custom-field values. Status stays in DRAFT; submit-for-review is
			// what transitions it.
			setStep('save', 'active')
			try {
				if (data.feedAd && data.storyAd) {
					await Promise.all([
						bakedInImageUpload({
							campaign: campaignId,
							data: {
								media: new File([data.feedAd], 'feed-ad.png', { type: 'image/png' }),
								collection: 'baked-feed',
							},
						}),
						bakedInImageUpload({
							campaign: campaignId,
							data: {
								media: new File([data.storyAd], 'story-ad.png', { type: 'image/png' }),
								collection: 'baked-story',
							},
						}),
					])
				}

				const filledCustomFields = Object.keys(data.custom_fields ?? {})
					.filter((key: string) => data.custom_fields[key]?.trim())
					.map((key: string) => ({ id: key, value: data.custom_fields[key] }))

				await campaignMutation({
					campaign: campaignId,
					data: {
						channels: data.channels,
						// Sent as `undefined` rather than via a conditional spread so the key
						// names stay type-checked: TypeScript does not excess-property-check
						// keys that arrive through a spread, which is how these survived the
						// mapper flip still snake_cased. `undefined` is dropped by
						// JSON.stringify, so the wire payload is unchanged when omitted.
						budget: hasMetaChannel ? eurosToCents(data.budget) : undefined,
						startDate: hasMetaChannel ? toLocalISOString(data.startDate) : undefined,
						endDate: hasMetaChannel ? toLocalISOString(data.endDate) : undefined,
						// `meta` is an opaque JSON column; the backend reads
						// `predicted_budget` from it literally, so this key is not mapped.
						meta: hasMetaChannel
							? { predicted_budget: data.predictedBudget ? eurosToCents(data.predictedBudget) : null }
							: undefined,
						customFields: filledCustomFields.length > 0 ? filledCustomFields : undefined,
					},
				})
			} catch (error) {
				logger.error(error, {
					tags: { feature: 'campaign-editor', action: 'persist-campaign-details' },
					data: { campaignId },
				})
				setStep('save', 'error', [genericError])
				throw error
			}
			setStep('save', 'done')

			// Step 2 — Check: confirm the campaign is ready for review. Surfaces the
			// backend's per-field issues to the user without attempting payment.
			setStep('check', 'active')
			let check
			try {
				check = (await campaignStatusCheck(campaignId)).data
			} catch (error) {
				logger.error(error, {
					tags: { feature: 'campaign-editor', action: 'check-campaign' },
					data: { campaignId },
				})
				setStep('check', 'error', [genericError])
				throw error
			}
			if (!check.valid) {
				const messages = check.issues.map((issue) => issue.message)
				setStep('check', 'error', messages.length > 0 ? messages : [genericError])
				throw new Error(messages[0] ?? genericError)
			}
			setStep('check', 'done')

			// Step 3 — Authorise: only when the user is paying by card. Wallet/invoice
			// and the no-payment case skip this entirely.
			let paymentMethods = data.paymentMethods
			const stripeIndex = paymentMethods.findIndex((entry) => entry.type === 'stripe')
			const stripeAmount = stripeIndex >= 0 ? paymentMethods[stripeIndex].amount : null

			if (data.stripePaymentMethodId && stripeIndex >= 0 && typeof stripeAmount === 'number' && jobillaOrgId) {
				setStep('authorise', 'active')
				try {
					const paymentIntentId = await authorisePaymentMethod({
						organisationId: jobillaOrgId,
						paymentMethodId: data.stripePaymentMethodId,
						amountCents: stripeAmount,
						campaignId,
						profileId: data.profileId ?? null,
					})
					const next = [...paymentMethods]
					next[stripeIndex] = { ...next[stripeIndex], paymentIntentId }
					paymentMethods = next
				} catch (error) {
					const translated = translatePaymentError(error, t, currencyFormatter)
					setStep('authorise', 'error', [translated ?? genericError])
					throw error
				}
				setStep('authorise', 'done')
			} else {
				setStep('authorise', 'skipped')
			}

			// Step 4 — Submit: create the wallet hold / capture the authorisation and
			// transition the campaign to REVIEW.
			setStep('submit', 'active')
			try {
				await submitForReviewMutation({
					campaign: campaignId,
					data: {
						paymentMethods,
						profileId: data.profileId ?? null,
						inventoryItems: data.inventoryItems ?? [],
					},
				})
			} catch (error) {
				const status = isAxiosError(error) ? error.response?.status : undefined
				const responseData = (isAxiosError(error) ? error.response?.data : undefined) as
					| { error?: unknown; context?: Record<string, unknown> }
					| undefined
				const responseCode = typeof responseData?.error === 'string' ? responseData.error : undefined

				logger.track('Editor: Campaign submit-for-review failed', {
					data: { status, error_code: responseCode, context: responseData?.context ?? null },
				})

				if (responseCode === 'campaign_limit_reached') {
					logger.warn('Campaign limit reached during submit-for-review', {
						tags: { feature: 'campaign-editor', action: 'campaign-limit-reached' },
						data: { campaignId },
					})
				} else {
					logger.error(error, {
						tags: { feature: 'campaign-editor', action: 'submit-for-review' },
						data: { campaignId, error_code: responseCode },
					})
				}

				const translated = translatePaymentError(error, t, currencyFormatter)
				setStep('submit', 'error', [translated ?? genericError])
				throw translated ? new Error(translated) : error
			}
			setStep('submit', 'done')

			// Optimistically increment campaign usage so the quota display is correct
			// the moment the dashboard renders, without waiting for a refetch.
			if (jobillaOrgId) {
				queryClient.setQueryData<EntitlementIndexResponse>(
					entitlementIndexQueryKey(jobillaOrgId),
					(old) => old ? { ...old, campaignUsage: (old.campaignUsage ?? 0) + 1 } : old,
				)
			}

			// Prefetch dashboard query so it's ready when the user navigates.
			// Optional — graceful degradation if it fails.
			try {
				// Params must match the dashboard's campaigns query exactly — the
				// derived cache key is what makes this prefetch land in its entry.
				await queryClient.fetchQuery(campaignsIndexQueryOptions({
					sort: ['created_at'],
					'filter[organisation_id]': jobillaOrgId ?? undefined,
					'filter[is_archived]': 'false',
				}))

				queryClient.invalidateQueries({
					queryKey: campaignsShowQueryKey(campaignId),
				})

				if (jobillaOrgId) {
					queryClient.invalidateQueries({
						queryKey: entitlementIndexQueryKey(jobillaOrgId),
					})
				}
			} catch (error) {
				logger.warn('Failed to prefetch dashboard query', {
					category: 'editor',
					tags: { feature: 'editor', action: 'prefetch-dashboard' },
					data: {
						message: error instanceof Error ? error.message : String(error),
						name: error instanceof Error ? error.name : undefined,
					},
				})
			}

			// TEMPORARY: Redirect immediately after prefetch to avoid editor error.
			// When campaign status changes to 'review', the editor can't display it
			// anymore and will show an error on next refetch. We navigate away first.
			void navigate({
				to: '/company/$companyId',
				params: { companyId },
			})
		})

		setOnRedirectAfterComplete(async () => {
			void navigate({
				to: '/company/$companyId',
				params: { companyId },
			})
		})
	}, [
		campaignId,
		setOnReviewComplete,
		setOnRedirectAfterComplete,
		navigate,
		bakedInImageUpload,
		campaignMutation,
		submitForReviewMutation,
		companyId,
		jobillaOrgId,
		queryClient,
		t,
		currencyFormatter,
		setCompletionSteps,
		setStep,
	])
}
